import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';

// Open dialogs, innermost last. Escape and the scroll lock act on the top one
// only, so a sheet opened from inside another sheet (e.g. a SelectField inside
// a form dialog) closes on its own.
const stack: symbol[] = [];

/**
 * M3 dialog — a bottom sheet on narrow screens, a centred modal from `sm`.
 *
 * Mobile follows the M3 modal bottom sheet: full-screen scrim that fades in,
 * a sheet that slides up with 28px top corners, a drag handle, no outline or
 * header rule, and safe-area padding so content clears the home indicator.
 *
 * Escape closes (top-most only), focus moves into the panel on open and returns
 * to the trigger on close, and background scroll is locked while open.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const id = Symbol('dialog');
    stack.push(id);
    const restore = document.activeElement as HTMLElement | null;
    // Focus the panel itself rather than guessing at a first field — the panel
    // is programmatically focusable, so screen readers announce the title.
    panelRef.current?.focus();

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && stack[stack.length - 1] === id) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      stack.splice(stack.indexOf(id), 1);
      document.body.style.overflow = prevOverflow;
      restore?.focus?.();
    };
    // onClose is read through a ref so a parent re-render (new inline callback)
    // doesn't re-run this effect and yank focus back to the panel mid-typing.
  }, [open]);

  if (!open) return null;

  // Portal to <body>: an ancestor with a transform (page enter animation) would
  // otherwise become the containing block for `position: fixed`, leaving the
  // scrim short of the top of the screen.
  return createPortal(
    <div
      className="dialog-scrim fixed inset-0 z-[100] flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        /* focus-visible:ring-0 — the panel is focused programmatically on open
           purely so screen readers land inside it. The app-wide
           `*:focus-visible` ring would otherwise draw a teal outline round the
           whole dialog, which reads as an error state. */
        className="dialog-panel flex max-h-[88dvh] w-full max-w-lg flex-col rounded-t-[28px] bg-[hsl(var(--card))] shadow-[0_-8px_32px_rgba(0,0,0,0.18)] outline-none focus-visible:outline-none focus-visible:ring-0 sm:max-h-[90vh] sm:rounded-[28px] sm:border sm:border-border/50 sm:shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle — visual affordance only (mobile). */}
        <div aria-hidden className="flex justify-center pt-3 sm:hidden">
          <span className="h-1 w-8 rounded-full bg-[hsl(var(--muted-foreground)/0.4)]" />
        </div>
        <header className="flex shrink-0 items-center justify-between gap-3 px-6 pb-2 pt-3 sm:pt-5">
          <h2 className="text-[1.375rem] font-normal leading-tight">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={dv.common.close}
            className="-me-2 grid size-10 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted/70 active:bg-muted"
          >
            <Icon name="close" size={22} />
          </button>
        </header>
        <div
          className="overflow-y-auto overscroll-contain px-6 pb-6 pt-2"
          style={{ paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom) + 1rem))' }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
